import React, { useState, useEffect, useRef } from 'react';
import { Respondent } from '../types';
import { SECTOR_OPTIONS, PRIVACY_URL } from '../constants';

interface ChatUnlockProps {
  onComplete: (data: Respondent) => void;
  onPreview: () => void;
  isOpen: boolean;
  isSubmitting: boolean;
}

const STORAGE_KEY = 'ddg_scan_profile';

const ChatUnlock: React.FC<ChatUnlockProps> = ({ onComplete, onPreview, isOpen, isSubmitting }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<Respondent>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : { name: '', email: '', position: '', company: '', sector: '', consent: false };
  });
  const [error, setError] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isFirstRender = useRef(true);

  // Focus management: Avoid auto-scrolling by not focusing on the very first mount
  useEffect(() => {
    if (isOpen && inputRef.current && !isFirstRender.current) {
      inputRef.current.focus({ preventScroll: true }); 
    }
    isFirstRender.current = false;
  }, [step, isTransitioning]);

  const steps = [
    { key: 'name', label: 'Full Name', type: 'text', placeholder: 'e.g., Jane Smith' },
    { key: 'email', label: 'Work Email Address', type: 'email', placeholder: 'jane@company.com' },
    { key: 'position', label: 'Current Job Title', type: 'text', placeholder: 'e.g., Head of Strategy' },
    { key: 'company', label: 'Primary Company', type: 'text', placeholder: 'Company Name' },
    { key: 'sector', label: 'Target Fintech Sector', type: 'select' },
    { key: 'consent', label: 'Authorize Report Generation', type: 'checkbox' }
  ];

  const currentStep = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  const handleNext = () => {
    const value = data[currentStep.key as keyof Respondent];
    if (currentStep.key === 'email' && !/^\S+@\S+\.\S+$/.test(value as string)) {
      setError('VALID WORK EMAIL REQUIRED'); return;
    } else if (currentStep.key === 'consent' && !value) {
      setError('AUTHORIZATION REQUIRED'); return;
    } else if (!value || (typeof value === 'string' && value.length < 2)) {
      setError('FIELD IS REQUIRED'); return;
    }
    setError('');
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    if (step < steps.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => { setStep(step + 1); setIsTransitioning(false); }, 250);
    } else onComplete(data);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 p-6 md:p-10 bg-[#0a0a0f] border-t border-white/10 shadow-[0_-30px_100px_rgba(0,0,0,0.8)] rounded-t-[2.5rem] transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
      <div className="max-w-xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1">
            <h2 className="text-xl font-[900] text-white uppercase italic tracking-tight mb-1">Verify Identity</h2>
            <p className="text-[9px] text-gray-600 font-black uppercase tracking-[0.4em]">Strategic Report Access</p>
          </div>
          <button onClick={onPreview} className="text-[9px] text-gray-500 hover:text-primary transition-colors font-black uppercase tracking-widest px-4 py-2">Preview Analysis</button>
        </div>

        <div className="w-full h-1 bg-white/5 rounded-full mb-8 overflow-hidden">
          <div className="h-full bg-primary shadow-[0_0_15px_rgba(130,109,236,0.6)] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
        </div>

        <div className={`min-h-[150px] transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-[0.25em] mb-4">{currentStep.label}</label>
          
          {currentStep.type === 'text' || currentStep.type === 'email' ? (
            <input
              ref={inputRef}
              type={currentStep.type}
              placeholder={currentStep.placeholder}
              className={`w-full p-5 rounded-xl border-2 bg-white/[0.03] text-lg font-bold text-white focus:bg-white/[0.05] transition-all outline-none ${error ? 'border-red-500/50' : 'border-white/5 focus:border-primary'}`}
              value={data[currentStep.key as keyof Respondent] as string}
              onChange={(e) => setData({ ...data, [currentStep.key]: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleNext()}
            />
          ) : currentStep.type === 'select' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SECTOR_OPTIONS.map(opt => (
                <button
                  key={opt}
                  onClick={() => { setData({ ...data, sector: opt }); setTimeout(handleNext, 100); }}
                  className={`p-3 rounded-xl border-2 text-[9px] font-black uppercase tracking-widest transition-all transform active:scale-95 ${data.sector === opt ? 'bg-primary border-primary text-white shadow-lg shadow-primary/30' : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/20'}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : (
            <label className="flex items-start cursor-pointer p-6 rounded-xl border-2 border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-all group">
              <input type="checkbox" className="h-5 w-5 text-primary border-white/10 rounded-lg focus:ring-primary bg-transparent cursor-pointer" checked={data.consent} onChange={(e) => setData({ ...data, consent: e.target.checked })} />
              <div className="ml-5">
                <p className="font-bold text-white mb-1 text-md">I authorize the transmission of my benchmark results.</p>
                <p className="text-gray-500 text-[9px] font-medium uppercase tracking-widest">Industry-standard data protection protocols apply.</p>
              </div>
            </label>
          )}
          {error && <p className="text-red-500 text-[9px] font-black uppercase tracking-widest mt-4 flex items-center gap-2">⚠️ {error}</p>}
        </div>

        <div className="mt-8 flex gap-3">
          {step > 0 && <button onClick={() => setStep(step - 1)} className="px-6 py-4 font-black text-gray-600 hover:text-white transition-colors uppercase text-[10px] tracking-widest">Back</button>}
          <button
            onClick={handleNext}
            disabled={isSubmitting}
            className="flex-1 bg-primary hover:bg-opacity-90 text-white font-[900] py-4 rounded-xl text-md uppercase italic tracking-widest glow-btn transition-all transform hover:-translate-y-1 active:scale-95 flex items-center justify-center"
          >
            {isSubmitting ? (
              <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            ) : step === steps.length - 1 ? 'Generate Report' : 'Next Step'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatUnlock;